import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * A flat grid: a rule above the header, a full-width rule under every row and
 * a vertical rule between columns, with no rounding. Pages run it flush by
 * leaving the gutter off the block that holds it, so the rules meet the
 * sidebar's edge and the window's.
 */
function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto border-t border-table-grid"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm font-normal", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("bg-table-header [&_tr]:border-b-0", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("bg-table", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
    return (
      <tfoot
        data-slot="table-footer"
        className={cn(
          "bg-table border-t font-normal [&>tr]:last:border-b-0",
          className
        )}
        {...props}
      />
    )
  }

  function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
    return (
      <tr
        data-slot="table-row"
        className={cn(
          "data-[state=selected]:bg-surface-selected transition-colors relative after:absolute after:bottom-0 after:inset-x-0 after:h-px after:bg-table-grid",
          className
        )}
        {...props}
      />
    )
  }

  function TableHead({ className, ...props }: React.ComponentProps<"th">) {
    return (
      <th
        data-slot="table-head"
        className={cn(
          "text-muted-foreground px-6 py-1 text-left align-middle font-normal whitespace-nowrap border-r border-table-grid last:border-r-0 [&:has([data-slot='checkbox-wrapper'])]:w-px [&:has([data-slot='checkbox-wrapper'])]:pl-checkbox-cell",
          className
        )}
        {...props}
      />
    )
  }

  function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-6 py-2 align-middle whitespace-nowrap font-normal border-r border-table-grid last:border-r-0 [&:has([data-slot='checkbox-wrapper'])]:w-px [&:has([data-slot='checkbox-wrapper'])]:pl-checkbox-cell",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm font-normal", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
